import { Box, Stack, TextField, Typography } from "@mui/material";
import { CustomerLoginCodeSelectSchema } from "@norskgassnett/db";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import AnonymousWrapper from "../lib/components/atomics/anonymous-wrapper";
import Loader from "../lib/components/atomics/loader";
import LoadingButton from "../lib/components/atomics/loading-button";
import MUIIcon from "../lib/components/atomics/mui-icon";
import CollapsableAlert from "../lib/components/complex/collapsible-alert";
import { useAuthContext } from "../lib/contexts/auth-context";
import { tk } from "../lib/i18n";
import { loginRoute } from "../router";

const LoginIdentifierInputSchema = z.union([
	CustomerLoginCodeSelectSchema.shape.identifier,
	z.string().transform((n, ctx) => {
		const match = n.match(/^(0047|\+47|47)?(\d{8})$/);
		if (!match?.[2]) {
			ctx.issues.push({
				code: "custom",
				message: tk("zod.telephoneFormatInvalid"),
				input: n,
			});

			return z.NEVER;
		}

		return `+47${match[2]}`;
	}),
]);

export const LoginSearchSchema = z.object({
	flowId: CustomerLoginCodeSelectSchema.shape.id.optional(),
	code: CustomerLoginCodeSelectSchema.shape.code.optional(),
	redirectTo: z.string().optional(),
});

export const Login = () => {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: loginRoute.fullPath });
	const { flowId, code } = loginRoute.useSearch();

	const { completeLoginFlow } = useAuthContext();

	useEffect(() => {
		if (flowId && code) {
			completeLoginFlow({ flowId: flowId, code: code });
		} else if (code) {
			navigate({
				search: (prev) => ({ redirectTo: prev.redirectTo }),
			});
		}
	});

	return (
		<AnonymousWrapper
			backFunction={
				flowId
					? () => {
							navigate({ to: loginRoute.fullPath });
						}
					: false
			}
		>
			<Typography component="h1" variant="h5">
				{t("login.title")}
			</Typography>
			<Box sx={{ mt: 1, width: 1 }}>
				{code ? (
					<Loader />
				) : !flowId ? (
					<StartLoginFlowComponent />
				) : (
					<CompleteLoginFlowComponent />
				)}
			</Box>
		</AnonymousWrapper>
	);
};

export default Login;

const StartLoginFlowComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate({ from: loginRoute.fullPath });

	const { startLoginFlow } = useAuthContext();
	const [customError, setCustomError] = useState<string>();

	const form = useForm({
		defaultValues: {
			identifier: "",
		},
		onSubmit: async ({ value }) => {
			const parsedIdentifier = LoginIdentifierInputSchema.safeParse(
				value.identifier,
			);

			if (!parsedIdentifier.success) return;

			const res = await startLoginFlow({
				identifier: parsedIdentifier.data,
			});

			if (!res.flowId) {
				setCustomError(res.error);
				return;
			}

			await navigate({
				search: (prev) => ({ ...prev, flowId: res.flowId }),
			});
		},
	});

	return (
		<Box sx={{ mt: 1, width: 1 }}>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<Stack spacing={2}>
					<form.Field
						name="identifier"
						validators={{
							onChange: ({ value, fieldApi }) =>
								!LoginIdentifierInputSchema.safeParse(value).success
									? t(`login.${fieldApi.name}Invalid`)
									: undefined,
						}}
					>
						{(field) => {
							return (
								<TextField
									required
									fullWidth
									id={field.name}
									name={field.name}
									label={t(`login.${field.name}Input`)}
									autoComplete="username"
									autoFocus
									disabled={field.form.state.isSubmitting}
									value={field.state.value}
									onChange={(e) => field.handleChange(e.target.value)}
									onBlur={field.handleBlur}
									error={!!field.state.meta.errors.length}
								/>
							);
						}}
					</form.Field>
					<form.Subscribe selector={(state) => [state.fieldMeta]}>
						{([fieldMeta]) => (
							<CollapsableAlert
								alert={fieldMeta.identifier?.errors?.join("\n") || customError}
							/>
						)}
					</form.Subscribe>
					<form.Subscribe
						selector={(state) => [
							state.canSubmit,
							state.isSubmitting,
							state.isPristine,
						]}
					>
						{([canSubmit, isSubmitting, isPristine]) => {
							return (
								<LoadingButton
									type="submit"
									fullWidth
									sx={{ mt: 2, mb: 1 }}
									disabled={!canSubmit || isPristine}
									loading={isSubmitting}
									endIcon={<MUIIcon name="arrow_forward" />}
								>
									{t("login.sendCodeBtn")}
								</LoadingButton>
							);
						}}
					</form.Subscribe>
				</Stack>
			</form>
		</Box>
	);
};

const CompleteLoginFlowComponent = () => {
	const { t } = useTranslation();

	const { flowId, redirectTo } = loginRoute.useSearch();
	const { completeLoginFlow } = useAuthContext();

	const [customError, setCustomError] = useState<string>();

	const form = useForm({
		defaultValues: {
			code: "",
		},
		onSubmit: async ({ value }) => {
			const parsedCode = CustomerLoginCodeSelectSchema.shape.code.safeParse(
				value.code,
			);

			if (!parsedCode.success || !flowId) return;

			const res = await completeLoginFlow({
				code: parsedCode.data,
				flowId: flowId,
				redirectTo: redirectTo,
			});

			if (res.error) {
				setCustomError(res.error);
			}
		},
	});

	return (
		<Box sx={{ mt: 1, width: 1 }}>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<Stack spacing={2}>
					<form.Field
						name="code"
						validators={{
							onChange: ({ value, fieldApi }) =>
								!CustomerLoginCodeSelectSchema.shape.code.safeParse(value)
									.success
									? t(`login.${fieldApi.name}Invalid`)
									: undefined,
						}}
					>
						{(field) => {
							return (
								<TextField
									required
									fullWidth
									id={field.name}
									name={field.name}
									label={t(`login.${field.name}Input`)}
									autoComplete="one-time-code"
									autoFocus
									disabled={field.form.state.isSubmitting}
									value={field.state.value}
									onChange={(e) => {
										field.handleChange(e.target.value);
									}}
									onBlur={field.handleBlur}
									error={!!field.state.meta.errors.length}
								/>
							);
						}}
					</form.Field>
					<form.Subscribe selector={(state) => [state.fieldMeta]}>
						{([fieldMeta]) => (
							<CollapsableAlert
								alert={fieldMeta.code?.errors?.join("\n") || customError}
							/>
						)}
					</form.Subscribe>
					<form.Subscribe
						selector={(state) => [
							state.canSubmit,
							state.isSubmitting,
							state.isPristine,
						]}
					>
						{([canSubmit, isSubmitting, isPristine]) => {
							return (
								<LoadingButton
									type="submit"
									fullWidth
									sx={{ mt: 2, mb: 1 }}
									disabled={!canSubmit || isPristine}
									loading={isSubmitting}
									endIcon={<MUIIcon name="arrow_forward" />}
								>
									{t("login.loginBtn")}
								</LoadingButton>
							);
						}}
					</form.Subscribe>
				</Stack>
			</form>
		</Box>
	);
};
